<template>
  <MainLayout>

    <!-- ============================================================
         HERO SECTION
    ============================================================ -->
    <section class="relative min-h-screen flex items-center overflow-hidden bg-gradient-hero">
      <!-- Hex grid -->
      <div class="absolute inset-0 hex-pattern pointer-events-none"></div>
      <!-- Grid lines -->
      <div class="absolute inset-0 grid-pattern pointer-events-none opacity-60"></div>

      <!-- Ambient glow blobs -->
      <div class="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-secondary/8 rounded-full blur-[130px] pointer-events-none animate-pulse-soft"></div>
      <div class="absolute bottom-1/4 left-1/6 w-[350px] h-[350px] bg-primary-light/12 rounded-full blur-[100px] pointer-events-none animate-pulse-soft" style="animation-delay:2s"></div>

      <!-- Molecular DNA structure - right side decoration -->
      <div class="absolute right-6 top-1/2 -translate-y-1/2 w-[340px] h-[480px] pointer-events-none hidden 2xl:block opacity-20">
        <svg viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full">
          <path d="M70,0 Q110,30 150,60 Q185,90 150,120 Q110,150 70,180 Q30,210 70,240 Q110,270 150,300" stroke="#9AB4C8" stroke-width="2" fill="none"/>
          <path d="M150,0 Q110,30 70,60 Q35,90 70,120 Q110,150 150,180 Q185,210 150,240 Q110,270 70,300" stroke="#9AB4C8" stroke-width="2" fill="none"/>
          <line x1="70" y1="30" x2="150" y2="30" stroke="#9AB4C8" stroke-width="1.5" opacity="0.6"/>
          <line x1="70" y1="90" x2="150" y2="90" stroke="#9AB4C8" stroke-width="1.5" opacity="0.6"/>
          <line x1="70" y1="150" x2="150" y2="150" stroke="#9AB4C8" stroke-width="1.5" opacity="0.6"/>
          <line x1="70" y1="210" x2="150" y2="210" stroke="#9AB4C8" stroke-width="1.5" opacity="0.6"/>
          <line x1="70" y1="270" x2="150" y2="270" stroke="#9AB4C8" stroke-width="1.5" opacity="0.6"/>
          <circle cx="70"  cy="30"  r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="150" cy="30"  r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="70"  cy="90"  r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="150" cy="90"  r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="70"  cy="150" r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="150" cy="150" r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="70"  cy="210" r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="150" cy="210" r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="70"  cy="270" r="5" fill="#9AB4C8" opacity="0.8"/>
          <circle cx="150" cy="270" r="5" fill="#9AB4C8" opacity="0.8"/>
          <!-- Central molecular node -->
          <circle cx="110" cy="150" r="18" fill="none" stroke="#594C7B" stroke-width="1.5" opacity="0.5"/>
          <circle cx="110" cy="150" r="6"  fill="#9AB4C8" opacity="0.7"/>
        </svg>
      </div>

      <!-- Floating medical cross decorations -->
      <div class="absolute top-8 left-[10%] pointer-events-none hidden lg:block opacity-8">
        <svg viewBox="0 0 40 40" class="w-10 h-10" fill="white">
          <rect x="14" y="2" width="12" height="36" rx="3"/>
          <rect x="2" y="14" width="36" height="12" rx="3"/>
        </svg>
      </div>
      <div class="absolute bottom-32 right-[12%] pointer-events-none hidden lg:block opacity-5">
        <svg viewBox="0 0 40 40" class="w-16 h-16" fill="white">
          <rect x="14" y="2" width="12" height="36" rx="3"/>
          <rect x="2" y="14" width="36" height="12" rx="3"/>
        </svg>
      </div>

      <!-- Content -->
      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-16 items-center">

          <!-- Left: Text content -->
          <div>
            <!-- Tagline bar -->
            <div class="flex items-center gap-3 mb-6 animate-fade-up" style="animation-delay:0.1s">
              <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
              <span class="w-3 h-0.5 bg-secondary/50 rounded-full"></span>
              <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">
                Welcome to Orel Medical Supplies
              </span>
            </div>

            <h1 class="text-4xl sm:text-5xl xl:text-6xl font-black text-white leading-[1.05] mb-6 animate-fade-up" style="animation-delay:0.2s">
              Engineering<br/>
              <span class="text-secondary">With Integrity</span>
            </h1>

            <p class="text-white/65 text-base sm:text-lg leading-relaxed mb-8 max-w-xl animate-fade-up" style="animation-delay:0.3s">
              At Orel Medical Supplies, we are committed to excellence in providing top-notch medical supplies and services. Founded by a team of highly skilled biomedical engineers, we bring over 12 years of collective expertise to the forefront of the medical devices industry in Uganda.
            </p>

            <!-- CTA buttons -->
            <div class="flex flex-wrap items-center gap-4 mb-10 animate-fade-up" style="animation-delay:0.4s">
              <RouterLink to="/our-products" class="btn-white">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 15H4V9h16v11z"/></svg>
                Explore Products
              </RouterLink>
              <RouterLink to="/contact-us" class="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:border-secondary hover:text-secondary text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                Get a Quote
              </RouterLink>
            </div>

            <!-- Team avatars + product count -->
            <div class="flex items-center gap-6 animate-fade-up" style="animation-delay:0.5s">
              <div class="flex -space-x-3">
                <img
                  v-for="(img, i) in teamImages"
                  :key="i"
                  :src="img"
                  :alt="'Orel Medical team member ' + (i + 1)"
                  class="w-10 h-10 rounded-full border-2 border-primary-dark object-cover"
                  loading="lazy"
                />
              </div>
              <div class="border-l border-white/20 pl-6">
                <p class="text-2xl font-black text-white">1k+</p>
                <p class="text-white/55 text-xs uppercase tracking-widest">Medical Products</p>
              </div>
              <div class="border-l border-white/20 pl-6">
                <p class="text-2xl font-black text-white">12+</p>
                <p class="text-white/55 text-xs uppercase tracking-widest">Years Expertise</p>
              </div>
            </div>
          </div>

          <!-- Right: Hero image with floating badges -->
          <div class="relative flex justify-center lg:justify-end animate-fade-in" style="animation-delay:0.4s">
            <div class="relative max-w-[520px] w-full">
              <!-- Decorative rings -->
              <div class="absolute -inset-4 rounded-3xl border border-secondary/12 pointer-events-none"></div>
              <div class="absolute -inset-8 rounded-[2.5rem] border border-white/5 pointer-events-none"></div>

              <!-- Main image -->
              <div class="relative rounded-2xl overflow-hidden shadow-hero">
                <img
                  src="../assets/orel-imgs/home2.jpg"
                  alt="Orel Medical Supplies - Professional biomedical team"
                  class="w-full h-auto object-cover"
                  loading="eager"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-primary-darkest/50 via-transparent to-transparent pointer-events-none"></div>
              </div>

              <!-- Floating badge - expertise -->
              <div class="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-card-lg animate-float">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9 9H2l5.5 4-2.1 6.5L12 16l6.6 3.5L16.5 13 22 9h-7L12 2z"/></svg>
                  </div>
                  <div>
                    <p class="text-gray-400 text-[10px] uppercase tracking-wider">Expertise</p>
                    <p class="font-black text-gray-900 text-sm">12+ Years</p>
                  </div>
                </div>
              </div>

              <!-- Floating badge - certified -->
              <div class="absolute -top-4 -right-4 bg-gradient-primary text-white rounded-xl px-4 py-2.5 shadow-card-lg animate-float" style="animation-delay:1s">
                <div class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 text-secondary" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z"/></svg>
                  <span class="text-xs font-bold">Quality Certified</span>
                </div>
              </div>

              <!-- Floating badge - Uganda coverage -->
              <div class="absolute top-1/2 -right-6 -translate-y-1/2 bg-white rounded-2xl p-3 shadow-card-lg animate-float hidden lg:flex items-center gap-2" style="animation-delay:0.7s">
                <div class="w-8 h-8 bg-secondary/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-secondary-dark" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
                </div>
                <div>
                  <p class="text-[9px] text-gray-400 uppercase tracking-wider leading-none">Based in</p>
                  <p class="font-bold text-gray-800 text-xs">Kampala, UG</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ============================================================
         STATS BAR
    ============================================================ -->
    <section class="py-8 bg-gradient-primary relative overflow-hidden">
      <div class="absolute inset-0 dot-pattern opacity-20 pointer-events-none"></div>
      <!-- EKG line decoration -->
      <div class="absolute top-1/2 left-0 right-0 -translate-y-1/2 opacity-10 pointer-events-none overflow-hidden">
        <svg viewBox="0 0 1200 60" fill="none" class="w-full">
          <path class="ekg-path" d="M0,30 L200,30 L230,5 L255,55 L275,15 L295,50 L315,30 L500,30 L530,5 L555,55 L575,15 L595,50 L615,30 L1200,30" stroke="#9AB4C8" stroke-width="2"/>
        </svg>
      </div>
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4">
          <div
            v-for="stat in stats"
            :key="stat.label"
            class="flex flex-col items-center text-center group"
            data-aos="fade-up"
            :data-aos-delay="stat.delay"
          >
            <div class="w-14 h-14 rounded-2xl bg-white/10 group-hover:bg-white/20 flex items-center justify-center flex-shrink-0 mb-3 transition-colors duration-300">
              <component :is="'svg'" class="w-6 h-6 text-secondary" viewBox="0 0 24 24" fill="currentColor" v-html="stat.svgPath"></component>
            </div>
            <p class="text-4xl font-black text-white leading-none">{{ stat.value }}</p>
            <p class="text-white/60 text-xs mt-1.5 uppercase tracking-wider">{{ stat.label }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================
         ABOUT PREVIEW
    ============================================================ -->
    <section class="py-20 lg:py-28 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-20 items-center">

          <!-- Left: Image with decorations -->
          <div class="relative" data-aos="fade-right">
            <div class="absolute -top-6 -left-6 w-56 h-56 bg-secondary/10 rounded-3xl -z-10"></div>
            <div class="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/8 rounded-2xl -z-10"></div>
            <div class="absolute top-8 left-8 w-28 h-28 dot-pattern opacity-60 -z-10 rounded-xl"></div>
            <div class="relative rounded-3xl overflow-hidden shadow-card-lg">
              <img
                src="../assets/orel-imgs/image-030.jpg"
                alt="Orel Medical Supplies - Professional healthcare team"
                class="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
            <!-- Overlay stat card -->
            <div class="absolute bottom-6 right-6 bg-white rounded-2xl p-4 shadow-card-lg">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
                </div>
                <div>
                  <p class="font-black text-gray-900 text-lg leading-none">2022</p>
                  <p class="text-gray-400 text-xs mt-0.5">Founded in Uganda</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Text content -->
          <div data-aos="fade-left" data-aos-delay="150">
            <div class="section-label">About Orel Supplies</div>
            <h2 class="section-title mb-6">We Execute Our Ideas From The Start to Finish</h2>
            <p class="text-gray-500 leading-relaxed mb-5 text-justify">
              At Orel Supplies, we're more than just a company; we're pioneers envisioning the future of healthcare in Uganda. Established in 2022 by a dedicated team of biomedical engineers, our mission is to equip healthcare providers with the quality resources and expertise necessary to provide exceptional patient care.
            </p>
            <p class="text-gray-500 leading-relaxed mb-8 text-justify">
              Driven by our commitment to create impact, we merge state-of-the-art technology with tailored services, shaping the healthcare landscape through continuous innovation. At Orel, we're dedicated to transforming healthcare, one breakthrough at a time.
            </p>
            <!-- Mission and Vision -->
            <div class="grid sm:grid-cols-2 gap-4 mb-8">
              <div class="bg-primary/5 rounded-2xl p-5 border border-primary/10">
                <div class="flex items-center gap-2.5 mb-3">
                  <div class="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/></svg>
                  </div>
                  <span class="font-bold text-primary text-sm">Our Mission</span>
                </div>
                <p class="text-gray-600 text-sm leading-relaxed">To Revolutionize Healthcare Quality.</p>
              </div>
              <div class="bg-secondary/10 rounded-2xl p-5 border border-secondary/20">
                <div class="flex items-center gap-2.5 mb-3">
                  <div class="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                  </div>
                  <span class="font-bold text-secondary-dark text-sm">Our Vision</span>
                </div>
                <p class="text-gray-600 text-sm leading-relaxed">To Provide exceptional critical devices and services to enhance lives.</p>
              </div>
            </div>
            <RouterLink to="/about-us" class="btn-primary">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
              Learn More About Us
            </RouterLink>
          </div>

        </div>
      </div>
    </section>

    <!-- ============================================================
         WHY CHOOSE US
    ============================================================ -->
    <section class="py-20 lg:py-28 bg-[#f7f5fb] relative overflow-hidden">
      <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/4 rounded-full blur-[100px] pointer-events-none"></div>
      <div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/6 rounded-full blur-[80px] pointer-events-none"></div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-2xl mx-auto mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">Why Partner With Orel</div>
          </div>
          <h2 class="section-title">Trusted Expertise You Can Rely On</h2>
          <p class="section-subtitle">
            We combine biomedical engineering excellence with deep healthcare market knowledge to deliver solutions that make a real difference.
          </p>
        </div>

        <!-- Feature cards grid -->
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="feature in whyChooseUs"
            :key="feature.title"
            class="group bg-white rounded-2xl p-7 shadow-card hover:shadow-card-lg hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden"
            data-aos="fade-up"
            :data-aos-delay="feature.delay"
          >
            <!-- Large watermark number -->
            <span class="absolute top-3 right-4 text-6xl font-black text-primary/4 leading-none select-none">{{ feature.num }}</span>
            <!-- Icon -->
            <div class="w-14 h-14 rounded-2xl bg-primary/8 group-hover:bg-gradient-primary flex items-center justify-center mb-5 transition-all duration-300">
              <svg class="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" viewBox="0 0 24 24" fill="currentColor" v-html="feature.svgPath"></svg>
            </div>
            <h3 class="font-bold text-gray-900 text-base mb-3 group-hover:text-primary transition-colors duration-200">
              {{ feature.title }}
            </h3>
            <p class="text-gray-500 text-sm leading-relaxed">{{ feature.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================
         SERVICES SECTION
    ============================================================ -->
    <section class="py-20 lg:py-28 bg-white relative overflow-hidden">
      <div class="absolute top-1/2 left-0 -translate-y-1/2 w-[300px] h-[600px] bg-primary/3 rounded-r-full pointer-events-none"></div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-2xl mx-auto mb-14">
          <div class="flex justify-center">
            <div class="section-label">Our Latest Services</div>
          </div>
          <h2 class="section-title">Our Partners in Healthcare Excellence: Setting the Standard in Management</h2>
          <p class="section-subtitle">
            Our Dedicated Teams in Healthcare Solutions: Crafting Tailored Products to Align with Your Vision and Market Demands. With Our Support, You'll Attract Patients, Enhance Revenue Streams, and Attract New Collaborators.
          </p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="service in services"
            :key="service.title"
            class="group bg-white border border-gray-100 rounded-2xl p-7 shadow-card hover:shadow-card-lg hover:-translate-y-1.5 hover:border-primary/20 transition-all duration-300"
            data-aos="fade-up"
            :data-aos-delay="service.delay"
          >
            <div class="w-14 h-14 rounded-2xl bg-primary/8 group-hover:bg-gradient-primary flex items-center justify-center mb-5 transition-all duration-300">
              <svg class="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" viewBox="0 0 24 24" fill="currentColor" v-html="service.svgPath"></svg>
            </div>
            <h3 class="font-bold text-gray-900 text-base mb-3 group-hover:text-primary transition-colors duration-200">
              {{ service.title }}
            </h3>
            <p class="text-gray-500 text-sm leading-relaxed mb-5">{{ service.desc }}</p>
            <RouterLink to="/services" class="inline-flex items-center gap-1.5 text-primary text-sm font-semibold group-hover:gap-2.5 transition-all duration-300">
              Learn more
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
            </RouterLink>
          </div>
        </div>

        <div class="text-center mt-10" data-aos="fade-up">
          <RouterLink to="/services" class="btn-outline">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/></svg>
            View All Services
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- ============================================================
         PRODUCTS PREVIEW
    ============================================================ -->
    <section class="py-20 lg:py-28 bg-[#f7f5fb] overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
          <div>
            <div class="section-label">Our Latest Products</div>
            <h2 class="section-title">Some Of Our Products</h2>
          </div>
          <RouterLink to="/our-products" class="btn-outline flex-shrink-0">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 15H4V9h16v11z"/></svg>
            View All Products
          </RouterLink>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="product in products"
            :key="product.name"
            class="product-card group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100"
            data-aos="fade-up"
            :data-aos-delay="product.delay"
          >
            <div class="relative overflow-hidden bg-gray-50 aspect-square">
              <img
                :src="product.image"
                :alt="product.name + ' - Orel Medical Supplies'"
                class="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <span class="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-wider text-primary bg-white px-3 py-1 rounded-full shadow-md border border-primary/10">
                {{ product.category }}
              </span>
              <div class="product-overlay absolute inset-0 bg-gradient-to-t from-primary-darkest/85 via-primary/30 to-transparent flex items-end justify-center pb-8">
                <RouterLink to="/our-products" class="bg-white text-primary text-xs font-bold px-6 py-2.5 rounded-xl hover:bg-primary hover:text-white transition-all duration-300 shadow-md">
                  View Details
                </RouterLink>
              </div>
            </div>
            <div class="p-5">
              <p class="text-[10px] font-bold uppercase tracking-widest text-secondary-dark mb-1.5">{{ product.category }}</p>
              <h4 class="font-bold text-gray-900 text-base group-hover:text-primary transition-colors duration-200">{{ product.name }}</h4>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================
         HEALTHCARE COMMITMENT SECTION
    ============================================================ -->
    <section class="relative py-20 lg:py-24 bg-gradient-hero overflow-hidden">
      <div class="absolute inset-0 hex-pattern opacity-60 pointer-events-none"></div>
      <!-- EKG line running across the section -->
      <div class="absolute top-10 left-0 right-0 opacity-15 pointer-events-none overflow-hidden">
        <svg viewBox="0 0 1440 50" fill="none" class="w-full">
          <path class="ekg-path" d="M0,25 L300,25 L340,5 L365,45 L385,2 L408,48 L430,25 L700,25 L740,5 L765,45 L785,2 L808,48 L830,25 L1440,25" stroke="#9AB4C8" stroke-width="1.5"/>
        </svg>
      </div>
      <!-- Molecular decoration -->
      <div class="absolute -left-20 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 pointer-events-none">
        <svg viewBox="0 0 200 200" fill="none" class="w-full h-full">
          <circle cx="100" cy="100" r="40" stroke="#9AB4C8" stroke-width="2"/>
          <circle cx="100" cy="100" r="70" stroke="#9AB4C8" stroke-width="1" stroke-dasharray="6 4"/>
          <circle cx="100" cy="30"  r="10" fill="#9AB4C8" opacity="0.6"/>
          <circle cx="165" cy="140" r="10" fill="#9AB4C8" opacity="0.6"/>
          <circle cx="35"  cy="140" r="10" fill="#9AB4C8" opacity="0.6"/>
          <circle cx="100" cy="100" r="8"  fill="#9AB4C8"/>
        </svg>
      </div>
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left: Heading + values -->
          <div data-aos="fade-right">
            <div class="flex items-center gap-3 mb-5">
              <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
              <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Our Commitment</span>
            </div>
            <h2 class="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              Committed to Uganda's Healthcare Transformation
            </h2>
            <p class="text-white/65 text-base leading-relaxed mb-8">
              Our commitment to excellence is evident in everything we do. From sourcing the highest quality medical supplies to offering comprehensive services such as clinical training, tender document preparations, electrical installations, and biomedical consultancy, we strive to exceed the expectations of our customers.
            </p>
            <p class="text-white/65 text-base leading-relaxed mb-8">
              Explore our website to learn more about our range of services and products, and discover how Orel Medical Supplies Ltd can be your trusted partner in delivering superior healthcare solutions.
            </p>
            <RouterLink to="/contact-us" class="btn-white">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
              Get in Touch
            </RouterLink>
          </div>
          <!-- Right: Core values grid -->
          <div class="grid grid-cols-2 gap-4" data-aos="fade-left" data-aos-delay="150">
            <div
              v-for="val in coreValues"
              :key="val.label"
              class="bg-white/8 hover:bg-white/15 border border-white/10 rounded-2xl p-5 transition-all duration-300 group"
            >
              <div class="w-10 h-10 bg-secondary/20 rounded-xl flex items-center justify-center mb-3">
                <svg class="w-5 h-5 text-secondary" viewBox="0 0 24 24" fill="currentColor" v-html="val.svgPath"></svg>
              </div>
              <h4 class="font-bold text-white text-sm mb-1.5">{{ val.label }}</h4>
              <p class="text-white/50 text-xs leading-relaxed">{{ val.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================================================
         CTA SECTION
    ============================================================ -->
    <section class="relative py-20 bg-gradient-cta overflow-hidden">
      <div class="absolute inset-0 hex-pattern opacity-20 pointer-events-none"></div>
      <div class="absolute right-1/4 top-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-5" data-aos="fade-up">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Get in Touch</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h2 class="text-4xl sm:text-5xl font-black text-white mb-5 leading-tight" data-aos="fade-up" data-aos-delay="100">
          Ready to Experience Excellence in Medical Supplies?
        </h2>
        <p class="text-white/65 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="200">
          Ready to experience excellence in medical supplies and services? Contact us today to learn more about how Orel Medical Supplies Ltd can support your healthcare facility.
        </p>
        <div class="flex flex-wrap justify-center gap-4" data-aos="fade-up" data-aos-delay="300">
          <RouterLink to="/contact-us" class="btn-white">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
            Contact Us Today
          </RouterLink>
          <RouterLink to="/our-products" class="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:border-white hover:bg-white/10 text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 15H4V9h16v11z"/></svg>
            Browse Products
          </RouterLink>
        </div>
      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import MainLayout from './Layout/MainLayout.vue'

const teamImages = [
  require('../assets/orel-imgs/image-002.jpg'),
  require('../assets/orel-imgs/image-007.jpg'),
  require('../assets/orel-imgs/image-027.jpg'),
  require('../assets/orel-imgs/image-023.jpg'),
  require('../assets/orel-imgs/image-041.jpg'),
]

/* SVG path inner HTML for inline stat icons */
const stats = [
  {
    value: '10+',  label: 'Team Members',    delay: 0,
    svgPath: '<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>',
  },
  {
    value: '1k+',  label: 'Products',         delay: 100,
    svgPath: '<path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 15H4V9h16v11z"/>',
  },
  {
    value: '200+', label: 'Customer Reviews',  delay: 200,
    svgPath: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
  },
  {
    value: '12+',  label: 'Years Expertise',   delay: 300,
    svgPath: '<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>',
  },
]

const whyChooseUs = [
  {
    num: '01',
    title: 'Biomedical Engineering Expertise',
    desc:  'Founded by certified biomedical engineers with over 12 years of combined expertise in Uganda\'s healthcare technology sector.',
    delay: 0,
    svgPath: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
  },
  {
    num: '02',
    title: 'Quality-Certified Products',
    desc:  'Every product we supply meets strict industry standards and regulatory requirements to ensure patient safety and clinical reliability.',
    delay: 100,
    svgPath: '<path d="M12 2L4 5v6.1c0 4.6 3.4 8.9 8 10 4.6-1.1 8-5.4 8-10V5L12 2zm-2 13l-3-3 1.4-1.4L10 12.2l5.6-5.6L17 8l-7 7z"/>',
  },
  {
    num: '03',
    title: 'End-to-End Service Support',
    desc:  'From procurement and installation to clinical training and after-sales assistance - our team is with you at every step of the journey.',
    delay: 200,
    svgPath: '<path d="M12 1c-5 0-9 4-9 9v4c0 1.1.9 2 2 2h2v-7H5v-1c0-3.9 3.1-7 7-7s7 3.1 7 7v1h-2v7h2c1.1 0 2-.9 2-2v-4c0-5-4-9-9-9z"/>',
  },
  {
    num: '04',
    title: 'Procurement Compliance',
    desc:  'We navigate complex healthcare procurement regulations so your facility stays compliant and audit-ready at all times.',
    delay: 300,
    svgPath: '<path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zm-8.5-5.5l-2-2-1.5 1.5L8.5 16l5.5-5.5-1.5-1.5-4 4z"/>',
  },
]

const services = [
  {
    title: 'Trainings',
    desc:  'Professional education in healthcare equipment, infection control, and technology integration.',
    delay: 0,
    svgPath: '<path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>',
  },
  {
    title: 'Tender Document Preparations',
    desc:  'Streamlined medical procurement, ensuring compliance with regulations and industry standards.',
    delay: 100,
    svgPath: '<path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>',
  },
  {
    title: 'Electrical Installations',
    desc:  'Safe, efficient medical equipment electrical installation for healthcare facilities.',
    delay: 200,
    svgPath: '<path d="M7 2v11h3v9l7-12h-4l4-8z"/>',
  },
  {
    title: 'Biomedical Consultancy',
    desc:  'Expert consultancy in health facility design, biomedical services, and market entry facilitation.',
    delay: 300,
    svgPath: '<path d="M12 2c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>',
  },
]

const products = [
  { name: 'Digital Weighing Scale',    category: 'Medical Scales',      image: require('../assets/orel-imgs/hd/adult-digital-weighing-scale.png'), delay: 0   },
  { name: 'Glucometer',                category: 'Laboratory Equipment', image: require('../assets/orel-imgs/hd/glucometer.png'),                  delay: 100 },
  { name: 'Microscope',                category: 'Laboratory Equipment', image: require('../assets/orel-imgs/hd/microscope.png'),                  delay: 200 },
  { name: 'Electric ICU Beds',         category: 'Hospital Furniture',   image: require('../assets/orel-imgs/hd/obsteric-beds.png'),               delay: 300 },
  { name: 'Patient Transfer Stretcher',category: 'Hospital Furniture',   image: require('../assets/orel-imgs/hd/patient-transfer-stretcher.png'),  delay: 400 },
  { name: 'Portable Autoclave',        category: 'CSSD',                 image: require('../assets/orel-imgs/hd/portable-autoclave.png'),          delay: 500 },
]

const coreValues = [
  {
    label: 'Integrity',
    desc:  'Honest, transparent dealings in every supply chain and client interaction.',
    svgPath: '<path d="M12 2L4 5v6.1c0 4.6 3.4 8.9 8 10 4.6-1.1 8-5.4 8-10V5L12 2zm-2 13l-3-3 1.4-1.4L10 12.2l5.6-5.6L17 8l-7 7z"/>',
  },
  {
    label: 'Excellence',
    desc:  'Highest standards in product quality and service delivery without compromise.',
    svgPath: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
  },
  {
    label: 'Innovation',
    desc:  'Continuously evolving to bring the latest biomedical technology to Uganda.',
    svgPath: '<path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>',
  },
  {
    label: 'Patient Focus',
    desc:  'Every decision we make is anchored in improving patient outcomes and safety.',
    svgPath: '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>',
  },
]
</script>
